import React, { useState, useEffect, useRef } from 'react';
import { Expense, ExpenseType, Property, Revenue } from '../types';
import { storage } from '../services/storage';
import { useModal } from './ui/ModalProvider';
import { AddExpenseModal } from './modals/AddExpenseModal';
import { AddRevenueModal } from './modals/AddRevenueModal';
import { Plus, Edit2, Euro, TrendingUp, Loader, Tag } from 'lucide-react';

interface ExpensesTableViewProps {
  propertyId: string;
  property: Property;
  year: number;
  agencyId?: string;
  onExpenseSaved?: () => void;
  onRevenueSaved?: () => void;
}

const MONTHS = [
  { value: 1, label: 'Jan', fullLabel: 'Janvier' },
  { value: 2, label: 'Fév', fullLabel: 'Février' },
  { value: 3, label: 'Mars', fullLabel: 'Mars' },
  { value: 4, label: 'Avr', fullLabel: 'Avril' },
  { value: 5, label: 'Mai', fullLabel: 'Mai' },
  { value: 6, label: 'Juin', fullLabel: 'Juin' },
  { value: 7, label: 'Juil', fullLabel: 'Juillet' },
  { value: 8, label: 'Août', fullLabel: 'Août' },
  { value: 9, label: 'Sept', fullLabel: 'Septembre' },
  { value: 10, label: 'Oct', fullLabel: 'Octobre' },
  { value: 11, label: 'Nov', fullLabel: 'Novembre' },
  { value: 12, label: 'Déc', fullLabel: 'Décembre' }
];

const EXPENSE_TYPE_LABELS: Record<ExpenseType, { label: string; color: string }> = {
  RENT: { label: 'Loyers', color: 'bg-purple-100 text-purple-800' },
  CHARGES: { label: 'Charges', color: 'bg-blue-100 text-blue-800' },
  CLEANING: { label: 'Ménage', color: 'bg-green-100 text-green-800' },
  MAINTENANCE: { label: 'Entretien', color: 'bg-orange-100 text-orange-800' },
  INSURANCE: { label: 'Assurance', color: 'bg-cyan-100 text-cyan-800' },
  UTILITIES: { label: 'Utilities', color: 'bg-indigo-100 text-indigo-800' },
  SOFTWARE: { label: 'Logiciels', color: 'bg-pink-100 text-pink-800' },
  PLACID_COMMISSION: { label: 'Commission Placid', color: 'bg-amber-100 text-amber-800' },
  OTHER: { label: 'Autres', color: 'bg-slate-100 text-slate-800' }
};

export const ExpensesTableView: React.FC<ExpensesTableViewProps> = ({
  propertyId,
  property,
  year,
  agencyId,
  onExpenseSaved,
  onRevenueSaved
}) => {
  const { toast } = useModal();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [revenues, setRevenues] = useState<Revenue[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState<{ month: number; type: 'expense' | 'revenue' | 'new-expense'; category?: string } | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isRevenueModalOpen, setIsRevenueModalOpen] = useState(false);
  const cellRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    loadData();
  }, [propertyId, year]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Synchroniser les assets vers les dépenses récurrentes
      await storage.syncAssetsToExpenses(propertyId);
      
      // Charger toutes les dépenses de l'année (tous les mois + récurrentes)
      const allExpenses: Expense[] = [];
      for (let month = 1; month <= 12; month++) {
        const monthExpenses = await storage.getExpenses({
          propertyId,
          agencyId,
          year,
          month
        });
        allExpenses.push(...monthExpenses);
      }
      
      // Charger toutes les dépenses récurrentes (month = null)
      // On les charge pour un mois arbitraire, le filtre inclura automatiquement les récurrentes
      const recurringExpenses = await storage.getExpenses({
        propertyId,
        agencyId,
        year,
        month: 1 // On prend le premier mois, les récurrentes seront incluses
      });
      
      // Filtrer uniquement les récurrentes (month === null)
      const recurring = recurringExpenses.filter(e => e.isRecurring && e.month === null && e.year === null);
      // Ajouter uniquement celles qui ne sont pas déjà dans allExpenses
      recurring.forEach(rec => {
        if (!allExpenses.find(e => e.id === rec.id)) {
          allExpenses.push(rec);
        }
      });

      setExpenses(allExpenses);

      // Charger tous les revenus de l'année
      const allRevenues: Revenue[] = [];
      for (let month = 1; month <= 12; month++) {
        const monthRevenues = await storage.getRevenue(propertyId, year, month);
        allRevenues.push(...monthRevenues);
      }
      setRevenues(allRevenues);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Organiser les dépenses par catégorie et mois
  const getExpensesByMonth = () => {
    const expensesByMonth: { [month: number]: { [category: string]: Expense[] } } = {};
    const categories = new Set<string>();
    
    // Initialiser tous les mois
    for (let month = 1; month <= 12; month++) {
      expensesByMonth[month] = {};
    }

    expenses.forEach(expense => {
      const category = expense.category || 'Autres';
      categories.add(category);
      
      if (expense.isRecurring && (expense.month === null || expense.month === undefined)) {
        // Dépense récurrente : afficher dans tous les mois
        for (let month = 1; month <= 12; month++) {
          if (!expensesByMonth[month][category]) {
            expensesByMonth[month][category] = [];
          }
          // Ajouter seulement si pas déjà présent (éviter doublons)
          if (!expensesByMonth[month][category].find(e => e.id === expense.id)) {
            expensesByMonth[month][category].push(expense);
          }
        }
      } else if (expense.month) {
        // Dépense spécifique à un mois
        if (!expensesByMonth[expense.month][category]) {
          expensesByMonth[expense.month][category] = [];
        }
        expensesByMonth[expense.month][category].push(expense);
      }
    });

    return { expensesByMonth, categories: Array.from(categories).sort() };
  };

  const { expensesByMonth, categories } = getExpensesByMonth();

  // Obtenir le montant total pour une catégorie dans un mois
  const getMonthExpenseTotal = (month: number, category: string): number => {
    return expensesByMonth[month][category]?.reduce((sum, e) => sum + e.amount, 0) || 0;
  };

  // Obtenir le revenu pour un mois
  const getMonthRevenue = (month: number): Revenue | null => {
    return revenues.find(r => r.month === month) || null;
  };

  // Obtenir le total des dépenses pour un mois
  const getMonthTotalExpenses = (month: number): number => {
    let total = 0;
    categories.forEach(category => {
      total += getMonthExpenseTotal(month, category);
    });
    return total;
  };

  // Gérer le clic sur une cellule
  const handleCellClick = (month: number, category: string, existingExpense?: Expense) => {
    if (existingExpense) {
      // Modifier une dépense existante
      setEditingExpense(existingExpense);
      setIsExpenseModalOpen(true);
    } else {
      // Créer une nouvelle dépense
      const type = categories.includes(category) 
        ? expensesByMonth[month][category]?.[0]?.expenseType || 'OTHER'
        : 'OTHER';
      
      setEditingExpense({
        propertyId,
        agencyId,
        expenseType: type,
        category: category || 'Nouvelle dépense',
        amount: 0,
        year,
        month,
        status: 'PENDING',
        currency: 'EUR',
        isRecurring: false
      } as Expense);
      setIsExpenseModalOpen(true);
    }
  };

  const [editingRevenue, setEditingRevenue] = useState<Revenue | null>(null);

  // Gérer le clic sur une cellule de revenu
  const handleRevenueCellClick = (month: number) => {
    const existingRevenue = getMonthRevenue(month);
    setEditingRevenue(existingRevenue || {
      propertyId,
      agencyId,
      year,
      month,
      nightlyRatesTotal: 0,
      taxesCollected: 0,
      cleaningFeesCollected: 0,
      grossRevenue: 0,
      totalCommissions: 0,
      netRevenue: 0
    } as Revenue);
    setIsRevenueModalOpen(true);
  };

  const handleSaveExpense = async (expense: Expense) => {
    await storage.saveExpense(expense);
    toast.success('Dépense enregistrée');
    await loadData();
    setIsExpenseModalOpen(false);
    setEditingExpense(null);
    onExpenseSaved?.();
  };

  const handleSaveRevenue = async (revenue: Revenue) => {
    await storage.saveRevenue(revenue);
    toast.success('Revenus enregistrés');
    await loadData();
    setIsRevenueModalOpen(false);
    onRevenueSaved?.();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec année */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900">Vue annuelle {year}</h3>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setEditingExpense({
                propertyId,
                agencyId,
                expenseType: 'OTHER',
                category: 'Nouvelle dépense',
                amount: 0,
                year,
                month: null,
                status: 'PENDING',
                currency: 'EUR',
                isRecurring: true
              } as Expense);
              setIsExpenseModalOpen(true);
            }}
            className="px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors text-sm font-semibold flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Dépense récurrente
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase border-r border-slate-200 min-w-[200px]">
                Catégorie
              </th>
              {MONTHS.map(month => (
                <th key={month.value} className="px-3 py-3 text-center text-xs font-bold text-slate-700 uppercase border-r border-slate-200 last:border-r-0 min-w-[100px]">
                  {month.label}
                </th>
              ))}
              <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase bg-amber-50">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Ligne Revenus */}
            <tr className="border-b border-slate-100 hover:bg-green-50/50">
              <td className="sticky left-0 z-10 bg-white px-4 py-3 font-semibold text-slate-900 border-r border-slate-200">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  Revenus Nets
                </div>
              </td>
              {MONTHS.map(month => {
                const revenue = getMonthRevenue(month.value);
                return (
                  <td
                    key={month.value}
                    className="px-3 py-3 text-center border-r border-slate-100 last:border-r-0 cursor-pointer hover:bg-green-100 transition-colors"
                    onClick={() => handleRevenueCellClick(month.value)}
                  >
                    <div className="text-sm font-semibold text-green-600">
                      {revenue ? revenue.netRevenue?.toFixed(2) || '0.00' : '—'}
                    </div>
                    {revenue && (
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Brut: {revenue.grossRevenue?.toFixed(2)} €
                      </div>
                    )}
                  </td>
                );
              })}
              <td className="px-4 py-3 text-center bg-amber-50 font-bold text-green-600">
                {revenues.reduce((sum, r) => sum + (r.netRevenue || 0), 0).toFixed(2)} €
              </td>
            </tr>

            {/* Lignes de dépenses par catégorie */}
            {categories.map(category => {
              const categoryExpenses = expenses.filter(e => e.category === category);
              const firstExpense = categoryExpenses[0];
              const isRecurring = firstExpense?.isRecurring && (firstExpense.month === null || firstExpense.month === undefined);
              const totalForCategory = categories.reduce((sum, cat) => {
                let catTotal = 0;
                for (let month = 1; month <= 12; month++) {
                  catTotal += getMonthExpenseTotal(month, cat);
                }
                return sum + catTotal;
              }, 0);

              return (
                <tr key={category} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 font-medium text-slate-900 border-r border-slate-200">
                    <div className="flex items-center gap-2">
                      {isRecurring && (
                        <Tag className="w-3 h-3 text-blue-600" title="Récurrent" />
                      )}
                      {firstExpense && (
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${EXPENSE_TYPE_LABELS[firstExpense.expenseType]?.color || 'bg-slate-100 text-slate-800'}`}>
                          {EXPENSE_TYPE_LABELS[firstExpense.expenseType]?.label || 'Autre'}
                        </span>
                      )}
                      <span>{category}</span>
                    </div>
                  </td>
                  {MONTHS.map(month => {
                    const monthExpenses = expensesByMonth[month.value][category] || [];
                    const total = getMonthExpenseTotal(month.value, category);
                    const isRecurringCell = isRecurring && monthExpenses.length > 0 && monthExpenses[0].isRecurring && (monthExpenses[0].month === null || monthExpenses[0].month === undefined);

                    return (
                      <td
                        key={month.value}
                        className="px-3 py-3 text-center border-r border-slate-100 last:border-r-0 cursor-pointer hover:bg-amber-50 transition-colors relative group"
                        onClick={() => handleCellClick(month.value, category, monthExpenses[0])}
                      >
                        <div className="text-sm font-semibold text-slate-900">
                          {total > 0 ? `${total.toFixed(2)} €` : '—'}
                        </div>
                        {isRecurringCell && (
                          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100">
                            <Tag className="w-2.5 h-2.5 text-blue-500" title="Récurrent" />
                          </div>
                        )}
                        {monthExpenses.length > 1 && (
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {monthExpenses.length} dép.
                          </div>
                        )}
                        {total === 0 && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <Plus className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center bg-amber-50 font-bold text-slate-900">
                    {(() => {
                      let total = 0;
                      for (let month = 1; month <= 12; month++) {
                        total += getMonthExpenseTotal(month, category);
                      }
                      return total.toFixed(2);
                    })()} €
                  </td>
                </tr>
              );
            })}

            {/* Ligne Total Dépenses */}
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
              <td className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-slate-900 border-r border-slate-200">
                Total Dépenses
              </td>
              {MONTHS.map(month => (
                <td key={month.value} className="px-3 py-3 text-center border-r border-slate-200 last:border-r-0 text-red-600">
                  {getMonthTotalExpenses(month.value).toFixed(2)} €
                </td>
              ))}
              <td className="px-4 py-3 text-center bg-amber-100 font-bold text-red-600">
                {(() => {
                  let total = 0;
                  for (let month = 1; month <= 12; month++) {
                    total += getMonthTotalExpenses(month);
                  }
                  return total.toFixed(2);
                })()} €
              </td>
            </tr>

            {/* Ligne Solde Net */}
            <tr className="border-t-2 border-slate-300 bg-green-50 font-bold">
              <td className="sticky left-0 z-10 bg-green-50 px-4 py-3 text-slate-900 border-r border-slate-200">
                Solde Net
              </td>
              {MONTHS.map(month => {
                const revenue = getMonthRevenue(month.value);
                const revenueAmount = revenue?.netRevenue || 0;
                const expensesAmount = getMonthTotalExpenses(month.value);
                const net = revenueAmount - expensesAmount;
                return (
                  <td key={month.value} className={`px-3 py-3 text-center border-r border-slate-200 last:border-r-0 ${net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {net.toFixed(2)} €
                  </td>
                );
              })}
              <td className="px-4 py-3 text-center bg-amber-100 font-bold">
                {(() => {
                  const totalRevenue = revenues.reduce((sum, r) => sum + (r.netRevenue || 0), 0);
                  let totalExpenses = 0;
                  for (let month = 1; month <= 12; month++) {
                    totalExpenses += getMonthTotalExpenses(month);
                  }
                  const net = totalRevenue - totalExpenses;
                  return (
                    <span className={net >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {net.toFixed(2)} €
                    </span>
                  );
                })()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {isExpenseModalOpen && editingExpense && (
        <AddExpenseModal
          isOpen={isExpenseModalOpen}
          onClose={() => {
            setIsExpenseModalOpen(false);
            setEditingExpense(null);
          }}
          onSave={handleSaveExpense}
          expenseToEdit={editingExpense}
          propertyId={propertyId}
          properties={[property]}
          agencyId={agencyId}
          year={year}
          month={editingExpense.month || undefined}
        />
      )}

      {isRevenueModalOpen && editingRevenue && (
        <AddRevenueModal
          isOpen={isRevenueModalOpen}
          onClose={() => {
            setIsRevenueModalOpen(false);
            setEditingRevenue(null);
          }}
          onSave={handleSaveRevenue}
          revenueToEdit={editingRevenue}
          properties={[property]}
          agencyId={agencyId}
          year={year}
          month={editingRevenue.month || undefined}
        />
      )}
    </div>
  );
};
